import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import PermissionCheck from '../../../helpers/PermissionCheck'

const KEY_QUESTIONS = ['Safe', 'Effective', 'Caring', 'Responsive', 'Well-led']

const QualityStatementRow = ({ qs, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const [showReason, setShowReason] = useState(false)
  const [reason, setReason] = useState(qs.inactiveReason || '')
  const [saving, setSaving] = useState(false)

  const save = (isActive, inactiveReason) => {
    setSaving(true)
    API.put(`/cqc-centre/settings/quality-statement/${qs.key}`, { isActive, inactiveReason })
      .then((res) => {
        if (res.status === 200) {
          setToast?.({ message: `"${qs.title}" ${isActive ? 'activated' : 'deactivated'}` })
          onSaved?.()
          setShowReason(false)
        } else {
          setToast?.({ message: res.data?.error || 'Unable to update', severity: 'error' })
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSaving(false))
  }

  const handleToggle = (e) => {
    if (e.target.checked) {
      save(true)
    } else {
      setShowReason(true)
    }
  }

  return (
    <Stack sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <Typography variant="body2" fontWeight={600}>
            {qs.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {qs.summary}
          </Typography>
        </Stack>
        <Switch checked={qs.isActive} onChange={handleToggle} disabled={saving} />
      </Stack>
      {!qs.isActive && qs.inactiveReason && !showReason && (
        <Typography variant="caption" color="warning.main">
          Deactivated: {qs.inactiveReason}
        </Typography>
      )}
      {showReason && (
        <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Reason for deactivating"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            required
          />
          <Button size="small" onClick={() => setShowReason(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={!reason.trim() || saving}
            onClick={() => save(false, reason)}
          >
            Confirm
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

const CqcSettings = () => {
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()
  const [registrationStatus, setRegistrationStatus] = useState('not_applicable')
  const [qualityStatements, setQualityStatements] = useState([])
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    API.get('/cqc-centre/settings')
      .then((res) => {
        if (res.status === 200) {
          setRegistrationStatus(res.data.registrationStatus)
          setQualityStatements(res.data.qualityStatements)
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const handleRegistrationChange = (value) => {
    setRegistrationStatus(value)
    API.put('/cqc-centre/settings/registration-status', { registrationStatus: value }).then(
      (res) => {
        if (res.status === 200) setToast?.({ message: 'Registration status updated' })
      },
    )
  }

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Typography variant="title1">CQC Centre Settings</Typography>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="title3" sx={{ mb: 1 }}>
            Registration Status
          </Typography>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={registrationStatus}
              onChange={(e) => handleRegistrationChange(e.target.value)}
            >
              <MenuItem value="registered">CQC Registered</MenuItem>
              <MenuItem value="applying">Applying for CQC Registration</MenuItem>
              <MenuItem value="not_applicable">Not CQC regulated - using this voluntarily</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        <Alert severity="info" variant="outlined">
          Deactivating a quality statement removes it from your readiness score entirely, so a
          reason is required. It stays visible here (and can be included in your Inspection Pack) so
          it's never used to silently inflate the score.
        </Alert>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Stack>
              <Typography variant="title3">Registration Centre</Typography>
              <Typography variant="body2" color="text.secondary">
                Manage regulated activities, event exposure assessments and your Statement of
                Purpose for the 6 Dec 2027 events-registration deadline.
              </Typography>
            </Stack>
            <Button variant="outlined" onClick={() => navigate('/cqc-centre/registration')}>
              Open Registration Centre
            </Button>
          </Stack>
        </Paper>

        {!loading &&
          KEY_QUESTIONS.map((kq) => (
            <Paper key={kq} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="title3" sx={{ mb: 1 }}>
                {kq}
              </Typography>
              {qualityStatements
                .filter((qs) => qs.keyQuestion === kq)
                .map((qs) => (
                  <QualityStatementRow key={qs.key} qs={qs} onSaved={load} />
                ))}
            </Paper>
          ))}
      </Stack>
    </PermissionCheck>
  )
}

export default CqcSettings
